import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "./scroll";

const ease = [0.16, 1, 0.3, 1];
const lines = ["Websites", "built to the", "core."];

const MaskLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay: 0.35 + i * 0.14, ease }}
    >
      {children}
    </motion.span>
  </span>
);

const TiltFrame = ({ y }) => {
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 18 });
  const sry = useSpring(ry, { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 14);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 14);
  };
  const reset = () => { rx.set(0); ry.set(0); };
  return (
    <motion.div
      style={{ y, perspective: 1000 }}
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.4, delay: 0.6, ease }}
      className="relative mx-auto w-full max-w-sm lg:max-w-none"
    >
      <motion.div
        onMouseMove={onMove}
        onMouseLeave={reset}
        style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
        className="relative aspect-[4/5] overflow-hidden rounded-[2rem] border border-white/10 shadow-[0_40px_120px_-20px_rgba(37,99,235,0.45)]"
        data-testid="hero-image-frame"
      >
        <img src="/hero-work.jpg" alt="Laptop displaying a website design" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-[#030712]/20 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,transparent_0%,rgba(3,7,18,0.55)_70%)]" />
        <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between font-mono text-xs uppercase tracking-[0.2em] text-slate-200">
          <span>Design / Build</span>
          <span className="flex items-center gap-2"><span className="h-2 w-2 animate-pulse rounded-full bg-sky-400" />Live</span>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-32 pb-24 sm:pt-40 lg:min-h-screen">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[700px] w-[900px] -translate-x-1/2 rounded-full bg-blue-800/25 blur-[140px]" />
      <div className="hero-grid pointer-events-none absolute inset-0" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-[1.25fr_1fr]">
        <motion.div style={{ y: textY }}>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.2 }}
            className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-sky-300"
          >
            Core Websites — Custom web design
          </motion.p>
          <h1 data-testid="hero-title" className="font-display text-[clamp(3.2rem,9vw,8.5rem)] font-semibold leading-[0.92] tracking-[-0.04em] text-white">
            {lines.map((l, i) => (
              <MaskLine key={l} i={i} className={i === 2 ? "text-gradient italic font-light" : ""}>{l}</MaskLine>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1, ease }}
            className="mt-8 max-w-md text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            Clean, fast, modern websites designed and built by hand — plus optional monthly maintenance so your site always stays in shape.
          </motion.p>
          <motion.button
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.15, ease }}
            whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
            data-testid="hero-contact-button"
            onClick={() => scrollToId("contact")}
            className="group mt-10 inline-flex items-center gap-3 rounded-full bg-white py-2 pl-6 pr-2 font-medium text-[#030712]"
          >
            Get in touch
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-700 text-white transition-transform duration-500 group-hover:translate-y-1">
              <ArrowDown className="h-4 w-4" />
            </span>
          </motion.button>
        </motion.div>
        <TiltFrame y={imgY} />
      </div>
    </section>
  );
};
